<template>
	<div>
		<div class="form-actions-bar flex flex-row pl-3 items-center b3:gap-0 b4:gap-3 b5:gap-0 b7:gap-3">
			<CCheckbox
				ref="selectAllCheckbox"
				title="Select All"
				class="bulk-action-checkbox relative"
				:value="allFormsSelected"
				:is-indeterminate="isSelectAllIndeterminate"
				@change="(val) => selectAllToggle(val)"
			/>
			<CButton class="bulk-action-button text-1 b4:text-2 b5:text-1 b7:text-2" :disabled="!isPaidPlan" @click="showMoveToFolderDialog = true">
				<template #icon><Inline svg="folder" /></template>
				<span class="b3:show">Move</span>
			</CButton>
			<CButton v-if="showArchive" class="bulk-action-button text-1 b4:text-2 b5:text-1 b7:text-2" :disabled="!isPaidPlan" @click="showArchiveDialog = true">
				<template #icon><Inline svg="archive" /></template>
				<span class="b3:show">Archive</span>
			</CButton>
			<CButton v-if="showRestore" class="bulk-action-button text-1 b4:text-2 b5:text-1 b7:text-2" @click="restoreForms">
				<template #icon><Inline svg="reset" /></template>
				<span class="b3:show">Restore</span>
			</CButton>
			<CButton class="bulk-action-button text-1 b4:text-2 b5:text-1 b7:text-2" @click="showDeleteDialog = true">
				<template #icon><Inline svg="trash" /></template>
				<span class="b3:show">Delete</span>
			</CButton>
			<CButton class="bulk-action-button text-1 b4:text-2 b5:text-1 b7:text-2" @click="exportDetails">
				<template #icon><Inline svg="download" /></template>
				<span class="b3:show">Export</span><span class="b4:show"> Details</span>
			</CButton>
		</div>
		<ArchiveFormsDialog :show="showArchiveDialog" :show-error="showArchiveError" @close="closeArchiveDialog" @confirm-archive="archiveForms" />
		<DeleteFormsDialog :show="showDeleteDialog" :form-ids="selectedFormIds" @closed="closeDeleteDialog" @deleted="formsDeletedCloseDialog" />
		<MoveFormsToFolderContainer v-if="showMoveToFolderDialog" is-standalone :form-ids="selectedFormIds" @visible-change="(val) => showMoveToFolderDialog = val" />
	</div>
</template>

<script lang="ts">
	import { computed, defineComponent, ref } from 'vue';
	import CButton from 'src/components/interaction/Button.vue';
	import { globalStore } from 'src/stores/global-store';
	import type { UserFormMeta } from '@cognitoforms/types/server-types/core/services/user-form-meta';
	import { exportFormDetails } from '@cognitoforms/api/services/form-details-service';
	import CCheckbox from 'src/components/form-controls/Checkbox.vue';
	import ArchiveFormsDialog from 'src/components/dialogs/ArchiveFormsDialog.vue';
	import DeleteFormsDialog from 'src/components/dialogs/DeleteFormsDialog.vue';
	import MoveFormsToFolderContainer from 'src/components/navigation/context-menu/MoveFormsToFolderContainer.vue';

	export default defineComponent({
		components: {
			CButton,
			CCheckbox,
			ArchiveFormsDialog,
			DeleteFormsDialog,
			MoveFormsToFolderContainer
		},
		props: {
			selectedForms: {
				type: Array<UserFormMeta>,
				required: true
			},
			allFormsSelected: {
				type: Boolean,
				required: true
			},
			isSelectAllIndeterminate: {
				type: Boolean,
				default: false
			}
		},
		emits: ['archived', 'restored', 'close', 'toggle-select-all'],
		setup(props, { emit }) {
			const isPaidPlan = globalStore.organization.Plan.IsPaidPlan;
			const selectedFormIds = computed(() => props.selectedForms.map(x => x.Id));

			const showMoveToFolderDialog = ref(false);

			const showArchive = computed(() => props.selectedForms.every(x => !x.IsArchived));
			const showArchiveDialog = ref(false);
			const showArchiveError = ref(false);
			const closeArchiveDialog = () => {
				showArchiveDialog.value = false;
				showArchiveError.value = false;
			};
			const archiveForms = () => {
				try {
					globalStore.archiveForms(selectedFormIds.value);
					emit('archived', selectedFormIds.value);
					closeArchiveDialog();
				}
				catch {
					showArchiveError.value = true;
				}
			};

			const showRestore = computed(() => props.selectedForms.every(x => x.IsArchived));
			const restoreForms = () => {
				globalStore.restoreForms(selectedFormIds.value);
				emit('restored', selectedFormIds.value);
			};

			const exportDetails = () => {
				exportFormDetails(selectedFormIds.value).then((response) => {
					const blob = new Blob([response], { type: 'text/csv' });
					const link = document.createElement('a');
					link.href = URL.createObjectURL(blob);
					link.download = 'form-details.csv';
					link.click();
				});
			};

			const showDeleteDialog = ref(false);
			const closeDeleteDialog = () => {
				showDeleteDialog.value = false;
			};
			const formsDeletedCloseDialog = () => {
				closeDeleteDialog();
				emit('close');
			};

			const selectAllToggle = (val: boolean) => {
				emit('toggle-select-all', val);
			};

			return {
				selectedFormIds,
				showMoveToFolderDialog,

				showArchive,
				showArchiveDialog,
				showArchiveError,
				closeArchiveDialog,
				archiveForms,
				isPaidPlan,

				showRestore,
				restoreForms,

				exportDetails,

				showDeleteDialog,
				closeDeleteDialog,
				formsDeletedCloseDialog,

				selectAllToggle
			};
		}
	});
</script>

<style lang="scss">
.form-actions-bar {

	.bulk-action {

		&-checkbox {
			top: -1px;
			width: auto;
		}

		&-button {
			background: transparent;
			border: none;
			padding: $space__3 + $space__1 $space__3;
			-webkit-print-color-adjust: exact;
			color-adjust: exact;

			@include b-up($b3) {
				padding-right: 6px;
				padding-left: 6px;
			}

			@include b-up($b4) {
				padding-right: $space__3;
				padding-left: $space__3;
			}

			@include b-up($b5) {
				padding-right: 4px;
				padding-left: 2px;
			}

			@include b-up($b7) {
				padding-right: $space__3;
				padding-left: $space__3;
			}

			.cog-button__icon {
				color: var(--icon-subtle);
			}

			&[disabled] {
				color: var(--text-disabled);
			}
		}
	}
}
</style>